import { Spinner } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import fetchImageElement from 'utils/fetchImage';
import httpRequest from 'utils/httpRequest';
import contains from 'utils/contains';

import PhotoGridItem from './photoGridItem';
import InfiniteSquareGrid from '../squareGrid/infiniteSquareGrid';
import useEditorItemContext from '../../context/editorItemContext';
import { usePolotnoStore } from '../storeContext';

/**
 * Standard page size for photo grid pagination and preloading.
 * This constant ensures consistent behavior across photo grid components:
 * - Used as preload buffer size for smooth scrolling
 * - Referenced by ancestry media components for API pagination
 * - Balances performance and memory usage
 */
export const PHOTO_GRID_PAGE_SIZE = 50;

export const createGridItem = ({ id, label, url, thumbUrl, custom } = {}) => ({
  custom,
  id,
  label,
  url,
  thumbUrl,
});

export const PHOTO_GRID_ACTION = {
  FAVORITE: 'favorite',
  BACKGROUND: 'background',
  DELETE: 'delete',
};

// Prevent scrollbar jumping due to container height is extended as spinner spins.
const StyledSpinner = styled(Spinner)`
  overflow: hidden;
`;

const fetchImage = async (abortController, url) => {
  const response = await httpRequest(url, {
    mode: 'cors',
    signal: abortController.signal,
    retryInAnyCase: true,
  });
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const element = await fetchImageElement(blobUrl);
  return { blob, blobUrl, element };
};
// TODO Available alternative: ColumnGrid
// - Same as original Polotno implementation. Container is divided into columns.
//   All images are fitted to the width of the column.
// - Superb thumbnail handling, as the exact width is known.
// - No need to crop the images, but images with large height can take a lot of space.
// - Complicated computing needed to ensure all images will cover the scroll area.
//   Sizes of the images (or more precisely their aspect ratios) must be known.
// - End of the grid looks bad, as the columns have different height.
//   To make this better, new images should be added to the shortest column,
//   which further complicates the computing.
// TODO Available alternative: RowGrid
// - Canva uses this. Container is divided into rows. All images are fitted to the height of the row.
//   The row height is flexible in some range, so the the images can be easily added.
//   This is probably the best possible appearance for the user.
// - Bad thumbnail handling, the worst case needs the image to fill the whole container width.
// - No need to crop the images, but images with large width can take a lot of space.
// - Even more complicated computing needed to ensure all images will cover the scroll area.
//   Sizes of the images (or more precisely their aspect ratios) must be known.
// - Only the whole rows should be added, so the incomplete row is only on the end,
//   thus items from multiple `loadMore` calls need to be merged:
//   - `showIncompleteRow` must be disabled and those images must be moved to the next JustifiedGrid.
//     Only after API confirms there are no more items, `showIncompleteRow` can be enabled while `hasMore` is disabled.
//     The "incomplete images" in `showIncompleteRow` can be detected in difference in images and processedImage.
//   - This may require customizing JustifiedGrid, so the rows content can be pre-computed,
//     and new rows will be added only on the bottom.
//     Looks like, this works natively.
//   - JustifiedGrid rows prop can be used to render one row at the time,
//     but it's hard to pass the remaining images.
//     Computing this up-front would be better.
// - Libraries:
//   - best overall: https://github.com/josephj/react-justified-grid
//   - best API: https://cedricdelpoux.github.io/react-responsive-masonry/
//   - best GUI  https://github.com/AustinGomez/react-image-grid
//   - good code, but crops, not maintained: https://github.com/benhowell/react-grid-gallery/blob/master/src/Gallery.js
//   - https://github.com/desandro/masonry
//   - https://css-tricks.com/adaptive-photo-layout-with-flexbox/
//   - https://github.com/huyansheng3/rc-photo-grid
//   - https://github.com/miromannino/Justified-Gallery
//   - https://github.com/schlosser/pig.js
// TODO Smart reaction to items changes:
// - Scroll to top.
// - Slice new images to fit the first page.
// - Reuse any of the displayed images if they also appear on the new first page.
const PhotoGrid = ({
  columnCount,
  columnGap,
  empty,
  hasMore,
  iconSize,
  isVisible,
  isLoading,
  items,
  loadMore,
  onSelect,
  rowGap,
  threshold,
  imageSize,
  assetType,
  actions,
  selectedIds,
  numberOfInitialItems,
}) => {
  const polotnoStore = usePolotnoStore();
  const { config } = useEditorItemContext();
  const [abortController, setAbortController] = useState();
  const [itemCount, setItemCount] = useState(numberOfInitialItems);

  const pageType = useMemo(() => {
    return polotnoStore?.activePage?.custom?.originalPage?.type;
  }, [polotnoStore?.activePage?.custom?.originalPage?.type]);

  const [failedUrls, setFailedUrls] = useState({});
  const [fetchedUrls, setFetchedUrls] = useState({});
  const [, setFetchingUrls] = useState({});

  // Items that didn't failed during fetching.
  // It's important to remove failed items from the input.
  // For example if input of 10 is fetched and everything fails, next 10 must start to fetch.
  // These are all items available to be displayed, parent component must be asked to load more.
  const unfailedItems = useMemo(() => items.filter(item => !failedUrls[item.thumbUrl]), [
    items,
    failedUrls,
  ]);

  // Only the count of items, that is required by infinite scroller should be displayed.
  // Parent component is always allowed to send more of them, but only the necessary are displayed.
  // List contains items that should be fetched, but only those that are successful are displayed.
  const fetchingItems = useMemo(() => unfailedItems.slice(0, itemCount), [
    itemCount,
    unfailedItems,
  ]);

  // Preload images ahead of the visible area to reduce scrolling lag.
  // Fetch more items than currently displayed to prepare for smooth scrolling.
  // Buffer size matches PHOTO_GRID_PAGE_SIZE to ensure smooth page transitions.
  const preloadBuffer = PHOTO_GRID_PAGE_SIZE; // Number of additional items to preload
  const preloadingItems = useMemo(() =>
    unfailedItems.slice(0, itemCount + preloadBuffer), [
    itemCount,
    unfailedItems,
    preloadBuffer,
  ]);

  // Prepare the abort controller to cancel pending promises, when component is unmounted.
  useEffect(() => {
    const controller = new AbortController();
    setAbortController(controller);
    return () => controller.abort();
  }, []);

  // Wait for the abort controller and start fetching all items that hasn't been started yet.
  useEffect(() => {
    // Only sync when the abort controller is ready.
    if (abortController == null) {
      return;
    }
    // Set new fetching promises. Functional update is used to prevent dependency cycle.
    setFetchingUrls(currentFetchingUrls => {
      // Create new fetching promises for all items that already don't have one.
      // Use preloadingItems instead of fetchingItems to fetch ahead of visible area.
      const newFetchingUrls = preloadingItems.reduce((acc, item) => {
        const { thumbUrl } = item;
        if (!currentFetchingUrls[thumbUrl]) {
          // Promise is created, but not waited for. Fetching promises must be updated in sync.
          acc[thumbUrl] = true;
          (async () => {
            try {
              // Fetch the item.
              const image = await fetchImage(abortController, thumbUrl);
              // Add the fetched item to the map.
              setFetchedUrls(currentFetchedUrls => ({
                ...currentFetchedUrls,
                [thumbUrl]: image,
              }));
            } catch (error) {
              // When abort() is called, the fetch() promise rejects with an AbortError.
              // It's ok, don't do anything, component is unmounted.
              if (error.name === 'AbortError') {
                return;
              }
              // Add the failed item to the map.
              setFailedUrls(currentFailedUrls => ({
                ...currentFailedUrls,
                [thumbUrl]: true,
              }));
            }
          })();
        }
        return acc;
      }, {});
      // Add new fetching promises to the current ones.
      return { ...currentFetchingUrls, ...newFetchingUrls };
    });
  }, [abortController, preloadingItems]);

  // Filter the items that are successfully fetched, so they can be displayed.
  // Order of the props items is preserved, so it doesn't change with each reload.
  // Display all loaded images immediately without blocking on failed/slow images.
  // This prevents sequential loading bottlenecks that cause choppy scrolling.
  const displayedItems = useMemo(() => {
    return fetchingItems
      .map(item => ({ image: fetchedUrls[item.thumbUrl], item }))
      .filter(({ image }) => image != null);
  }, [fetchedUrls, fetchingItems]);

  // There is more items to display if the parent component have more items to display
  // or if there are items available to display that didn't started fetching.
  const hasMoreProxy = useMemo(() => {
    return hasMore || unfailedItems.length > itemCount;
  }, [unfailedItems, hasMore, itemCount]);

  // Items are loading if the parent component items are loading or there are some images fetching.
  // The latter is detected by comparing the count of items requested to fetch
  // and those successfully fetched and displayed.
  const isLoadingProxy = useMemo(() => isLoading || fetchingItems.length > displayedItems.length, [
    displayedItems,
    isLoading,
    fetchingItems,
  ]);

  // If the item count requested by infinite scroller exceeds available items,
  // more is requested from the parent component unless there are no more items available.
  useEffect(() => {
    if (hasMore && itemCount > unfailedItems.length) {
      loadMore({ itemCount });
    }
  }, [hasMore, itemCount, loadMore, unfailedItems]);

  // Set the requested item count when infinite scroller wants to load more.
  const loadMoreProxy = ({ itemCount: newItemCount }) => {
    setItemCount(val => Math.max(val, newItemCount));
  };

  const { showMenu, actionProps } = useMemo(() => {
    const values = {
      backgroundable:
        contains(actions, [PHOTO_GRID_ACTION.BACKGROUND]) &&
        config.withPolotnoBackgrounds &&
        pageType === 'pages',
      favoritable: contains(actions, [PHOTO_GRID_ACTION.FAVORITE]) && assetType,
      deletable: contains(actions, [PHOTO_GRID_ACTION.DELETE]),
    };

    return {
      showMenu: Object.values(values).some(Boolean),
      actionProps: values,
    };
  }, [actions, assetType, pageType]);

  // If there are no available items here and in the parent component, the grid is empty.
  // Custom empty element is displayed if available.
  if (!isLoadingProxy && !hasMoreProxy && unfailedItems.length === 0) {
    if (empty != null && isVisible) {
      return empty;
    }
    return null;
  }

  return (
    <InfiniteSquareGrid
      columnCount={columnCount}
      columnGap={columnGap}
      hasMore={hasMoreProxy}
      isVisible={isVisible}
      isLoading={isLoadingProxy}
      items={displayedItems}
      loadMore={loadMoreProxy}
      loader={<StyledSpinner key={0} size={iconSize} />}
      renderItem={({ item: displayedItem, style }) => (
        <PhotoGridItem
          image={displayedItem.image}
          item={displayedItem.item}
          key={displayedItem.item.id}
          onClick={() => {
            onSelect({
              ...displayedItem,
              previewWidth: displayedItem.image?.element?.width,
              previewHeight: displayedItem.image?.element?.height,
            });
          }}
          style={style}
          size={imageSize}
          assetType={assetType}
          showMenu={showMenu}
          actions={actionProps}
          selected={!!selectedIds[displayedItem.item.id]}
        />
      )}
      rowGap={rowGap}
      threshold={threshold}
    />
  );
};

PhotoGrid.defaultProps = {
  columnCount: 2,
  columnGap: 5,
  empty: undefined,
  hasMore: false,
  iconSize: 40,
  isLoading: false,
  isVisible: true,
  loadMore: () => { },
  onSelect: () => { },
  rowGap: 5,
  // Threshold is raised because waiting for an uninterrupted sequence of fetched items takes time.
  // Item can't be displayed unless all items in front of them are fetched or failed.
  threshold: 500,
  imageSize: 'cover',
  assetType: undefined,
  actions: [],
  selectedIds: {},
  numberOfInitialItems: 0,
};

PhotoGrid.propTypes = {
  columnCount: PropTypes.number,
  columnGap: PropTypes.number,
  empty: PropTypes.object,
  hasMore: PropTypes.bool,
  iconSize: PropTypes.number,
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadMore: PropTypes.func,
  onSelect: PropTypes.func,
  rowGap: PropTypes.number,
  threshold: PropTypes.number,
  imageSize: PropTypes.oneOf(['cover', 'contain']),
  assetType: PropTypes.string,
  actions: PropTypes.array,
  selectedIds: PropTypes.object,
  numberOfInitialItems: PropTypes.number,
};

export default PhotoGrid;
